import { VideoShape } from '@/types';
import { useEffect, useRef, useState } from 'react';
import Lazy from '../Lazy';
import Button from '../Button';
import PlaySVG from '@/svg/play.svg?url';
import PauseSVG from '@/svg/pause.svg?url';
import classNames from 'classnames';

interface Props {
    video: VideoShape;
}

const TIME_TO_IDLE = 1;

const ConstructorVideoPlayer = ({ video }: Props) => {
    const videoEl = useRef<HTMLVideoElement>(null);
    const wrapperEl = useRef<HTMLButtonElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isIdle, setIsIdle] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        const video = videoEl.current;
        const wrapper = wrapperEl.current;

        const onPause = () => {
            setIsPlaying(false);

            video?.removeEventListener('canplay', onCanPlay);

            if (wrapper) {
                wrapper.removeEventListener('mousemove', resetTimer);
                wrapper.removeEventListener('click', resetTimer);
                wrapper.removeEventListener('touchstart', resetTimer);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setIsIdle(false);
        };

        const onCanPlay = () => {
            if (video) {
                video.play();
            }
        };

        const setTimer = () => {
            timeoutRef.current = setTimeout(() => {
                setIsIdle(true);
            }, 1000 * TIME_TO_IDLE);
        };

        const resetTimer = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setIsIdle(false);
            setTimer();
        };

        if (video) {
            if (isPlaying) {
                if (wrapper) {
                    wrapper.addEventListener('mousemove', resetTimer);
                    wrapper.addEventListener('click', resetTimer);
                    wrapper.addEventListener('touchstart', resetTimer);
                    wrapper.addEventListener('keypress', resetTimer);
                    setTimer();
                }

                if (video.readyState === 4) {
                    video.play();
                } else {
                    video.addEventListener('canplay', onCanPlay, { once: true });
                }
            } else {
                video.pause();
                video?.removeEventListener('canplay', onCanPlay);

                if (wrapper) {
                    wrapper.removeEventListener('mousemove', resetTimer);
                    wrapper.removeEventListener('click', resetTimer);
                    wrapper.removeEventListener('touchstart', resetTimer);
                }
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                setIsIdle(false);
            }

            video.addEventListener('pause', onPause);
            video.addEventListener('ended', onPause);
        }

        return () => {
            if (video) {
                video.removeEventListener('canplay', onCanPlay);
                video.removeEventListener('pause', onPause);
                video.removeEventListener('ended', onPause);
            }
            if (wrapper) {
                wrapper.removeEventListener('mousemove', resetTimer);
                wrapper.removeEventListener('click', resetTimer);
                wrapper.removeEventListener('touchstart', resetTimer);
            }
            setIsIdle(false);
        };
    }, [isPlaying]);

    return (
        <button
            className={classNames('constructor-video', {
                'constructor-video--playing': isPlaying,
                'constructor-video--idle': isIdle,
            })}
            ref={wrapperEl}
            onClick={() => {
                setIsPlaying((prev) => !prev);
            }}
        >
            {video && video.length > 0 && (
                <div className="constructor-video__video">
                    <Lazy>
                        <video playsInline ref={videoEl}>
                            {video.map((item, i) => {
                                return <source key={i} type={item.type} src={item.src} />;
                            })}
                        </video>
                    </Lazy>
                </div>
            )}
            <Button
                geometryVariant="mustache"
                icon={
                    isPlaying ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={PauseSVG} alt="" width={20} height={20} className="img-fluid" decoding="async" />
                    ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={PlaySVG} alt="" width={20} height={20} className="img-fluid" decoding="async" />
                    )
                }
                className="constructor-video__control"
                tag={'span'}
            />
        </button>
    );
};

export default ConstructorVideoPlayer;
